'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';

const ConfirmDialogContext = createContext(null);

function getConfirmButtonClassName(tone = 'danger') {
  switch (tone) {
    case 'primary':
      return 'app-button app-button--primary';
    case 'secondary':
      return 'app-button app-button--secondary';
    case 'ghost':
      return 'app-button app-button--ghost';
    case 'danger':
    default:
      return 'app-button app-button--danger';
  }
}

export function ConfirmDialogProvider({ children }) {
  const resolverRef = useRef(null);
  const [dialogState, setDialogState] = useState(null);

  const closeDialog = useCallback((result) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setDialogState(null);
    resolver?.(result);
  }, []);

  const requestConfirmation = useCallback((options = {}) => {
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialogState({
        title: options.title || 'Confirm Action',
        message: options.message || 'Are you sure you want to continue?',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        confirmTone: options.confirmTone || 'danger',
      });
    });
  }, []);

  useEffect(() => {
    return () => {
      if (resolverRef.current) {
        resolverRef.current(false);
        resolverRef.current = null;
      }
    };
  }, []);

  return (
    <ConfirmDialogContext.Provider value={requestConfirmation}>
      {children}
      <Modal
        show={Boolean(dialogState)}
        onHide={() => closeDialog(false)}
        centered
        className="app-confirm-dialog app-glass-modal"
        backdropClassName="app-glass-modal-backdrop"
        dialogClassName="app-confirm-dialog__dialog"
        contentClassName="app-confirm-dialog__content"
      >
        {dialogState ? (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{dialogState.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="app-confirm-dialog__message">
                {dialogState.message}
              </p>
              <div className="app-confirm-dialog__actions">
                <button
                  type="button"
                  className="app-button app-button--ghost"
                  onClick={() => closeDialog(false)}
                >
                  {dialogState.cancelLabel}
                </button>
                <button
                  type="button"
                  className={getConfirmButtonClassName(dialogState.confirmTone)}
                  onClick={() => closeDialog(true)}
                >
                  {dialogState.confirmLabel}
                </button>
              </div>
            </Modal.Body>
          </>
        ) : null}
      </Modal>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);

  if (!context) {
    throw new Error(
      'useConfirmDialog must be used within ConfirmDialogProvider',
    );
  }

  return context;
}
